import React, { useEffect, useState } from "react";
import {
  LogOut, Inbox, ShoppingBag, Package, LayoutDashboard,
  Plus, Pencil, Trash2, RefreshCw, Loader2, Download, Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/adminApi";
import { LOGOS } from "@/lib/api";
import { toast } from "sonner";
import { OrdersPanel } from "@/components/admin/OrdersPanel";
import { QuotesPanel } from "@/components/admin/QuotesPanel";
import { ErrorsPanel } from "@/components/admin/ErrorsPanel";
import { ListingsPanel } from "@/components/admin/ListingsPanel";
import { StripeHealthPanel } from "@/components/admin/StripeHealthPanel";

const fmtDate = (ts) => {
  if (!ts) return "-";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

const EMPTY = { name: "", category: "", brand: "", type: "hardware", condition: "new", price: "", description: "", image: "", badge: "" };

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-brand/10 rounded-2xl p-5 flex items-center gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="h-11 w-11 rounded-xl bg-brand-50 flex items-center justify-center">
        <Icon className="h-5 w-5 text-brand" />
      </div>
      <div>
        <p className="text-2xl font-heading font-bold text-brand leading-none">{value ?? "—"}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState({});
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [mapping, setMapping] = useState(null);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // product id or null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [importing, setImporting] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, i, o, p] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/inquiries"),
        adminApi.get("/admin/orders"),
        adminApi.get("/admin/products"),
      ]);
      setStats(s.data); setInquiries(i.data); setOrders(o.data); setProducts(p.data);
      try { const pr = await adminApi.get("/admin/projects"); setProjects(pr.data); } catch (e) { console.error("Failed to load website projects:", e); }
      try { const mp = await adminApi.get("/admin/product-lines/mapping"); setMapping(mp.data); } catch (e) { console.error("Failed to load product-line mapping:", e); }
    } catch {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, category: p.category, brand: p.brand || "", type: p.type, condition: p.condition || "new", price: String(p.price), description: p.description, image: p.image, badge: p.badge || "" });
    setDialogOpen(true);
  };

  const saveProduct = async () => {
    if (!form.name || !form.category || !form.price || !form.image) {
      toast.error("Name, category, price and image are required");
      return;
    }
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price), badge: form.badge || null };
    try {
      if (editing) {
        await adminApi.put(`/admin/products/${editing}`, payload);
        toast.success("Product updated");
      } else {
        await adminApi.post("/admin/products", payload);
        toast.success("Product added");
      }
      setDialogOpen(false);
      await loadAll();
    } catch {
      toast.error("Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplateCsv = () => {
    const header = "name,brand,category,type,condition,price,description,image,badge";
    const example = 'Catalyst 9200 Switch,Cisco,Networking,hardware,new,2499,"24-port managed switch",https://example.com/switch.jpg,Best Seller';
    const blob = new Blob([header + "\n" + example + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "jero-one-products-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/products/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} product(s)${data.errors?.length ? `, ${data.errors.length} skipped` : ""}`);
      if (data.errors?.length) data.errors.slice(0, 5).forEach((er) => toast.error(er));
      await loadAll();
    } catch {
      toast.error("Import failed. Check your CSV format.");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };


  const confirmDelete = async () => {
    try {
      await adminApi.delete(`/admin/products/${deleteId}`);
      toast.success("Product deleted");
      await loadAll();
    } catch {
      toast.error("Could not delete product");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen bg-brand-50/40" data-testid="admin-dashboard">
      <header className="bg-brand text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGOS.wordmarkGreen} alt="Jero-One" className="h-9 w-auto object-contain bg-white rounded px-1.5 py-1" />
            <span className="font-heading font-semibold hidden sm:block">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium leading-tight">{user?.name || "Admin"}</p>
              <p className="text-xs text-white/60">{user?.email}</p>
            </div>
            <Button data-testid="admin-refresh" onClick={loadAll} variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button data-testid="admin-logout" onClick={onLogout} variant="ghost" className="text-white hover:bg-white/10 rounded-full">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Inbox} label="Inquiries" value={stats.inquiries} />
          <StatCard icon={ShoppingBag} label="Orders" value={stats.orders} />
          <StatCard icon={LayoutDashboard} label="Paid Orders" value={stats.paid_orders} />
          <StatCard icon={Package} label="Products" value={stats.products} />
        </div>

        <Tabs defaultValue="inquiries">
          <TabsList className="bg-brand-50 mb-4">
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">Quotes</TabsTrigger>
            <TabsTrigger value="errors" data-testid="tab-errors">Errors</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments-health">Payments Health</TabsTrigger>
            <TabsTrigger value="listings" data-testid="tab-listings">Get Listed</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="lines" data-testid="tab-lines">Product Lines</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Websites</TabsTrigger>
          </TabsList>

          {loading && (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 text-brand animate-spin" /></div>
          )}

          {/* Inquiries */}
          <TabsContent value="inquiries">
            <div className="bg-white border border-brand/10 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="inquiries-table">
                  <thead className="bg-brand-50/60 text-brand text-left">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">Email</th>
                      <th className="px-4 py-3 font-semibold">Phone</th>
                      <th className="px-4 py-3 font-semibold">Message</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((q) => (
                      <tr key={q.id} className="border-t border-brand/5 align-top" data-testid="inquiry-row">
                        <td className="px-4 py-3"><Badge className="bg-brand-50 text-brand hover:bg-brand-50 capitalize">{(q.type || "contact").replace("_", " ")}</Badge></td>
                        <td className="px-4 py-3 font-medium">{q.name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{q.email}</td>
                        <td className="px-4 py-3 text-muted-foreground">{q.phone || "-"}</td>
                        <td className="px-4 py-3 max-w-sm text-muted-foreground">{q.message}</td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(q.ts)}</td>
                      </tr>
                    ))}
                    {!inquiries.length && !loading && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No inquiries yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders">
            <OrdersPanel orders={orders} loading={loading} />
          </TabsContent>
          <TabsContent value="quotes"><QuotesPanel /></TabsContent>
          <TabsContent value="errors"><ErrorsPanel /></TabsContent>
          <TabsContent value="payments"><StripeHealthPanel /></TabsContent>
          <TabsContent value="listings"><ListingsPanel /></TabsContent>

          {/* Products */}
          <TabsContent value="products">
            <div className="flex flex-wrap justify-end gap-2 mb-4">
              <Button data-testid="download-template-btn" onClick={downloadTemplateCsv} variant="outline" className="rounded-full border-brand/20 text-brand hover:bg-brand-50">
                <Download className="h-4 w-4 mr-1.5" /> CSV Template
              </Button>
              <label className={`inline-flex items-center gap-1.5 cursor-pointer rounded-full border border-brand/20 text-brand hover:bg-brand-50 px-4 py-2 text-sm font-medium ${importing ? "opacity-60 pointer-events-none" : ""}`} data-testid="import-csv-label">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {importing ? "Importing…" : "Import CSV"}
                <input type="file" accept=".csv" onChange={handleImport} className="hidden" data-testid="import-csv-input" />
              </label>
              <Button data-testid="add-product-btn" onClick={openCreate} className="rounded-full bg-brand hover:bg-brand-700 text-white">
                <Plus className="h-4 w-4 mr-1.5" /> Add Product
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.id} className="bg-white border border-brand/10 rounded-2xl overflow-hidden flex flex-col" data-testid="admin-product-card">
                  <img src={p.image} alt={p.name} className="h-36 w-full object-cover" />
                  <div className="p-4 flex flex-col flex-1">
                    <p className="text-xs text-brand-500 font-medium">{p.brand ? `${p.brand} · ${p.category}` : p.category}</p>
                    <h3 className="font-heading font-semibold text-brand text-sm leading-snug mt-0.5">{p.name}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      <p className="font-heading font-bold text-brand">${Number(p.price).toFixed(2)}</p>
                      <Badge className={`${(p.condition || "new") === "refurbished" ? "bg-amber-500 hover:bg-amber-500" : "bg-brand-500 hover:bg-brand-500"} text-white text-[10px]`}>
                        {(p.condition || "new") === "refurbished" ? "Refurbished" : "New"}
                      </Badge>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-brand/5">
                      <Button data-testid="edit-product-btn" onClick={() => openEdit(p)} variant="outline" size="sm" className="flex-1 rounded-full border-brand/20 text-brand hover:bg-brand-50">
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button data-testid="delete-product-btn" onClick={() => setDeleteId(p.id)} variant="outline" size="sm" className="rounded-full border-destructive/20 text-destructive hover:bg-destructive/5">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Product Lines mapping report */}
          <TabsContent value="lines">
            {!mapping ? (
              <div className="text-center py-10 text-muted-foreground">No mapping data.</div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="mapping-totals">
                  {[["Products", mapping.totals.products], ["Lines", mapping.totals.lines], ["Mapped", mapping.totals.mapped], ["Unmapped", mapping.totals.unmapped], ["Drafts", mapping.totals.drafts]].map(([k, v]) => (
                    <div key={k} className="bg-white border border-brand/10 rounded-xl p-4">
                      <p className="text-2xl font-heading font-bold text-brand">{v}</p>
                      <p className="text-xs text-muted-foreground">{k}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-white border border-brand/10 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="mapping-table">
                      <thead className="bg-brand-50/60 text-brand text-left">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Product Line</th>
                          <th className="px-4 py-3 font-semibold">Assigned variations</th>
                          <th className="px-4 py-3 font-semibold">Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mapping.lines.map((row) => (
                          <tr key={row.line.slug} className="border-t border-brand/5 align-top" data-testid="mapping-row">
                            <td className="px-4 py-3 font-medium whitespace-nowrap">{row.line.name}<span className="block text-xs text-muted-foreground">{row.line.category}</span></td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1.5">
                                {row.variants.map((v) => (
                                  <span key={v.id} className={`text-xs px-2 py-1 rounded-full border ${v.active && v.price > 0 ? "bg-brand-50 text-brand border-brand/10" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                    {v.specs || v.name}{(!v.active || v.price <= 0) ? " · draft" : ` · $${v.price}`}
                                  </span>
                                ))}
                                {!row.variants.length && <span className="text-xs text-muted-foreground">No variations</span>}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">{row.active_count}/{row.variant_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white border border-brand/10 rounded-2xl p-5">
                    <p className="font-heading font-semibold text-brand mb-2">Draft SKUs (need price)</p>
                    {mapping.draft_skus.length ? mapping.draft_skus.map((d) => (
                      <p key={d.id} className="text-sm text-muted-foreground">• {d.specs || d.name} <span className="text-xs">({d.line})</span></p>
                    )) : <p className="text-sm text-muted-foreground">None</p>}
                  </div>
                  <div className="bg-white border border-brand/10 rounded-2xl p-5">
                    <p className="font-heading font-semibold text-brand mb-2">Unmapped products</p>
                    {mapping.unmapped_products.length ? mapping.unmapped_products.map((u) => (
                      <p key={u.id} className="text-sm text-muted-foreground">• {u.name}</p>
                    )) : <p className="text-sm text-muted-foreground">None — all mapped</p>}
                  </div>
                  <div className="bg-white border border-brand/10 rounded-2xl p-5">
                    <p className="font-heading font-semibold text-brand mb-2">Duplicate products</p>
                    {mapping.duplicate_products.length ? mapping.duplicate_products.map((d) => (
                      <p key={d.name} className="text-sm text-muted-foreground">• {d.name} ×{d.ids.length}</p>
                    )) : <p className="text-sm text-muted-foreground">None</p>}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Website Projects */}
          <TabsContent value="projects">            <div className="bg-white border border-brand/10 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="projects-table">
                  <thead className="bg-brand-50/60 text-brand text-left">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Business</th>
                      <th className="px-4 py-3 font-semibold">Plan</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Email</th>
                      <th className="px-4 py-3 font-semibold">Fee</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((pr) => {
                      const paid = (pr.status || "").toLowerCase() === "paid";
                      const sp = pr.spec || {};
                      return (
                        <tr key={pr.id} className="border-t border-brand/5 align-top" data-testid="project-row">
                          <td className="px-4 py-3 font-medium">
                            <div className="flex items-center gap-2">
                              {sp.logoUrl && <img src={sp.logoUrl} alt="logo" className="h-7 w-7 rounded object-contain border border-brand/10" />}
                              {sp.businessName || "—"}
                            </div>
                          </td>
                          <td className="px-4 py-3">{pr.tier_name}</td>
                          <td className="px-4 py-3 capitalize text-muted-foreground">{sp.type || "-"}</td>
                          <td className="px-4 py-3 text-muted-foreground">{pr.email || "-"}</td>
                          <td className="px-4 py-3 whitespace-nowrap">${pr.fee}</td>
                          <td className="px-4 py-3">
                            <Badge className={paid ? "bg-brand-500 text-white hover:bg-brand-500" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>
                              {pr.status || "initiated"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(pr.ts)}</td>
                        </tr>
                      );
                    })}
                    {!projects.length && !loading && (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No website builder requests yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </main>
      {/* Product form dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div><Label>Name</Label><Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label><Input data-testid="product-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Networking" /></div>
              <div><Label>Brand</Label><Input data-testid="product-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="e.g. Cisco" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <select data-testid="product-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="hardware">hardware</option>
                  <option value="software">software</option>
                  <option value="service">service</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Condition</Label>
                <select data-testid="product-condition" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="new">new</option>
                  <option value="refurbished">refurbished</option>
                </select>
              </div>
              <div><Label>Price (USD)</Label><Input data-testid="product-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Badge (optional)</Label><Input data-testid="product-badge" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="e.g. New" /></div>
              <div><Label>Image URL</Label><Input data-testid="product-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://... or /legacy/router.jpg" /></div>
            </div>
            <div><Label>Description</Label><Textarea data-testid="product-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-product-btn" onClick={saveProduct} disabled={saving} className="rounded-full bg-brand hover:bg-brand-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Add product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>This removes it from your shop immediately. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="rounded-full bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
